import { useState } from 'react';

import { useQuery } from '@apollo/client';
import { MultiSelect, MultiSelectChangeEvent } from 'primereact/multiselect';

import { MIN_SEARCH_LENGTH, SELECTION_LIMIT } from '@/constants';
import { SEARCH_ACCOUNTS_MULTISELECT } from '@/types.gql';

interface IAccountMultiSelectProps {
  className?: string;
  label?: string;
  value: string[];
  onChange: (accounts: string[]) => void;
  disabled?: boolean;
  placeholder?: string;
  appendTo?: HTMLElement | null;
}

export const AccountMultiSelect = ({
  className,
  label,
  value,
  onChange,
  disabled = false,
  placeholder = 'Select accounts',
  appendTo,
}: IAccountMultiSelectProps) => {
  const [searchTerm, setSearchTerm] = useState<string>('');
  const query = useQuery(SEARCH_ACCOUNTS_MULTISELECT, {
    variables: {
      take: SELECTION_LIMIT,
      where: {
        and: [
          {
            or: [
              { id: { in: value } },
              ...(searchTerm
                ? [
                    { code: { contains: searchTerm } },
                    { name: { contains: searchTerm } },
                  ]
                : []),
            ],
          },
          { stateCode: { eq: 0 } },
        ],
      },
    },
  });
  const formattedAccounts =
    query.data?.accounts?.items?.map((account) => ({
      id: account.id,
      name: `${account.code}`,
    })) || [];

  const handleFilter = (event: { filter: string }) => {
    if (event.filter.length >= MIN_SEARCH_LENGTH) {
      setSearchTerm(event.filter);
    }
  };

  const handleChange = (e: MultiSelectChangeEvent) => {
    onChange?.(e.value);
  };

  return (
    <div className={`account-multi-select ${className || ''}`}>
      {label && <label className="mb-2 block">{label}</label>}
      <MultiSelect
        value={value}
        options={formattedAccounts}
        onChange={handleChange}
        optionLabel="name"
        optionValue="id"
        filter
        filterPlaceholder="Search accounts..."
        onFilter={handleFilter}
        placeholder={placeholder}
        disabled={disabled}
        className="w-full"
        display="chip"
        appendTo={appendTo}
      />
    </div>
  );
};
